import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import {
  Message, Dimmer, Loader, Button,
} from 'semantic-ui-react';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { isEmpty, find } from 'lodash';
import ScrollToTop from 'react-scroll-up';
import moment from 'moment';
import { FormattedNumber } from 'react-intl';
import LoadingDataEnhancer from './LoadingData';
import { utcMoment, dateTimeFormatted } from '../utilities/dateUtils';

import VehicleDetails from '../views/VehicleStatusTracker/VehicleDetails';
import HeaderBlock from '../views/VehicleStatusTracker/HeaderBlock';
import auctionLocationsQuery from '../graphql/auctionLocationsQuery';

const api = axios.create({
  baseURL: process.env.REACT_APP_VST_API_URL,
  timeout: process.env.REACT_APP_VST_TIMEOUT,
  headers: {
    'X-API-KEY': process.env.REACT_APP_VST_API_KEY,
    'content-type': 'application/json',
  },
});

const retryCondition = error => {
  return error.code === 'ECONNABORTED';
};

axiosRetry(api, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  shouldResetTimeout: true,
  retryCondition,
});

class VehicleStatusTracker extends Component {
  constructor(props) {
    super(props);
    this.state = {
      vehicleRecord: null,
      error: null,
      loading: true,
      activeIndex: [0],
      allClosed: false,
    };
  }

  closeAll = () => {
    this.setState({ activeIndex: [], allClosed: true });
  };

  openAll = () => {
    this.setState({ activeIndex: [0, 1, 2, 3, 4, 5, 6], allClosed: false });
  };

  handleClick = (e, itemProps) => {
    const { index } = itemProps;
    const { activeIndex } = this.state;
    let newState;

    if (activeIndex.indexOf(index) > -1) {
      newState = activeIndex.filter(i => i !== index);
    } else {
      newState = [...activeIndex, index];
    }

    this.setState({ activeIndex: newState });
  };

  componentDidMount() {
    const { match: { params: { purchaseId } } } = this.props;

    api
      .get('/', {
        params: {
          tappyPurchaseId: purchaseId,
        },
      })
      .then(res => {
        const vehicleRecord = res.data.Items[0];
        const count = res.data.Count;
        if (count === 0) {
          this.setState({
            vehicleRecord,
            loading: false,
            error: 'VST Data is not available for this guarantee.',
          });
        } else {
          this.setState({
            vehicleRecord,
            loading: false,
            error: null,
          });
        }
      })
      .catch(e => {
        this.setState({ error: e.message, loading: false });
      });
  }

  render() {
    const {
      vehicleRecord, error, loading, activeIndex, allClosed,
    } = this.state;
    const {
      data: { locations },
    } = this.props;

    const isAuctionEdgeLocation = vehicleRecord
      && !isEmpty(find(locations, ['initials', vehicleRecord.locationCode]))
      && !isEmpty(
        find(locations, ['initials', vehicleRecord.locationCode])
          .auctionedge_code
      );

    const formatField = field => {
      let value = vehicleRecord[field]
        ? vehicleRecord[field].toString()
        : 'NULL';

      if (value === 'NULL' || value === '-1') {
        value = '';
      }
      return value;
    };

    const expireGuaranteeGreater = record => {
      const expireDateWithSixtyDays = dateTimeFormatted(
        utcMoment(record.expireDate.toString()).add('days', 60)
      );
      const updatedOnFormatted = dateTimeFormatted(
        utcMoment(record.updatedOn)
      );
      return updatedOnFormatted > expireDateWithSixtyDays;
    };

    const formatDate = field => {
      let value = vehicleRecord[field]
        ? vehicleRecord[field].toString()
        : 'NULL';

      if (value === 'NULL' || value === '-1') {
        value = '';
      } else {
        value = utcMoment(value).format('MM/DD/YYYY');
      }
      return value;
    };

    const formatDateTime = field => {
      let value = vehicleRecord[field]
        ? vehicleRecord[field].toString()
        : 'NULL';
      if (value === 'NULL' || value === '-1') {
        value = '';
      } else {
        value = moment(value)
          .utc()
          .local()
          .format('MM/DD/YYYY ,h:mm:ss a');
      }
      return value;
    };

    const currencyProps = price => {
      return {
        currency: 'USD',
        style: 'currency',
        minimumFractionDigits: 2,
        value: price,
      };
    };

    const negativeCurrency = price => {
      return (
        '$('
        + parseFloat(price)
          .toFixed(2)
          .replace('-', '')
          .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
        + ')'
      );
    };

    const formatCurrency = field => {
      const value = vehicleRecord[field]
        ? vehicleRecord[field].toString()
        : 'NULL';

      if (value === 'NULL' || value === '-1') {
        return '';
      } if (value.includes('-')) {
        return negativeCurrency(value);
      }
      return React.createElement(FormattedNumber, currencyProps(value));
    };

    const convertCase = status => {
      return isEmpty(status) ? status : status.toLowerCase();
    };

    const paymentStatus = record => {
      const paymentStatusVal = convertCase(record.origVehPaymentStatus);
      const floorplan = record.origVehFloorplanCompCode;
      const floorplanStatus = convertCase(
        record.origVehFloorPlanPaymentStatus
      );
      let status = '';
      if (
        paymentStatusVal
          === (convertCase('PAID') || convertCase('DEALER PAID'))
        && floorplanStatus === convertCase('Floorplan Paid (Funded)')
      ) {
        status = 'Floorplan Paid (Funded)';
      } else if (
        paymentStatusVal === convertCase('UNPAID')
        && floorplanStatus === convertCase('Floorplan Paid (Unfunded)')
      ) {
        status = 'Floorplan Paid (Unfunded)';
      } else if (
        paymentStatusVal === convertCase('UNPAID')
        && floorplanStatus === convertCase('Floorplan Paid (Partially Funded)')
      ) {
        status = 'Floorplan Paid (Partially Funded)';
      } else if (
        paymentStatusVal
          === (convertCase('PAID') || convertCase('DEALER PAID'))
        && (floorplan === null || floorplan === '-1')
      ) {
        status = 'Dealer Paid';
      } else if (paymentStatusVal === convertCase('UNPAID')) {
        status = 'Unpaid';
      } else {
        status = paymentStatusVal;
      }
      return status;
    };

    const vehicleNetColourComparison = record => {
      const sumOfAllPrices = record.origVehCertificationFee
        + record.origVehOutsideFloorFee
        + record.origVehOVETransactionFee
        + record.origVehBuyersFee
        + record.origVehDealshieldFee
        + record.origVehSalePrice
        + record.origVehTransactionSimulcastFee
        + record.origVehTransactionLateFee
        + record.origVehTransactionFloorPlanFee
        + record.origVehTransactionPSIFee
        + record.origVehElementPremiumBuyFee
        + record.origVehToyotaAdminFee
        + record.origVehTaxFee
        + record.origVehTranPromoDiscountFee
        + record.origVehTranTransportAssistancFee
        + record.origVehTranGoodwillFee
        + record.origVehTranMiscellaneousFee
        + record.origVehDSRebillingAmount
        + record.origVehPSIRebillingAmount;
      return record.origVehNet === sumOfAllPrices ? 'green' : 'red';
    };

    return (
      <div>
        {error && (
          <Message content={error} negative />
        )}
        {loading && (
          <Dimmer inverted active style={{ height: 300 }}>
            <Loader indeterminate>Loading...</Loader>
          </Dimmer>
        )}
        {vehicleRecord && (
          <div>
            <HeaderBlock
              vehicleRecord={vehicleRecord}
              allClosed={allClosed}
              closeAll={this.closeAll}
              openAll={this.openAll}
              paymentStatus={paymentStatus}
              formatDateTime={formatDateTime}
              expireGuaranteeGreater={expireGuaranteeGreater}
              convertCase={convertCase}
            />
            <VehicleDetails
              vehicleRecord={vehicleRecord}
              isAuctionEdgeLocation={isAuctionEdgeLocation}
              activeIndex={activeIndex}
              paymentStatus={paymentStatus}
              handleClick={this.handleClick}
              formatField={formatField}
              formatDate={formatDate}
              formatDateTime={formatDateTime}
              formatCurrency={formatCurrency}
              vehicleNetColourComparison={vehicleNetColourComparison}
            />
          </div>
        )}
        <ScrollToTop showUnder={160}>
          <Button className='vst-close-button'>Back to Top</Button>
        </ScrollToTop>
      </div>
    );
  }
}

VehicleStatusTracker.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      purchaseId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  data: PropTypes.shape({
    locations: PropTypes.array,
  }).isRequired,
};

export default compose(
  withRouter,
  graphql(auctionLocationsQuery, 'auctionLocations'),
  LoadingDataEnhancer
)(VehicleStatusTracker);
